import React from 'react';
import { X } from 'lucide-react';
import ResponseTargets from './ResponseTargets';
import InterruptPanel from './InterruptPanel';

// Covers the viewport including sidebar and topbar. Peripheral navigation
// chrome during a timed trial is a measurement problem, not a style choice.
export default function RunnerShell({ task, runner, title, onExit }) {
  const { phase, stage, block, blockCount, trialIndex, feedback, selectedTargetId } = runner;
  const trialsInBlock = block?.trials?.length || 0;
  const blocksDone = block ? block.blockIndex : 0;
  const withinBlock = trialsInBlock ? (trialIndex + 1) / trialsInBlock : 0;
  const overall = blockCount ? (blocksDone + withinBlock) / blockCount : 0;
  const currentTrial = block?.trials?.[trialIndex];
  // A chained task draws one persistent field for the whole block. Gating it on
  // the stimulus stage would unmount it between steps, blinking the trail.
  const showStimulus =
    phase === 'running' && Boolean(currentTrial) &&
    (stage === 'stimulus' || task.chainOnset === true);
  // The cue persists into the stimulus phase by default: a cued switching task
  // is not a memory task, and the participant must never have to recall which
  // rule applies. A task that sets cuePersists: false gets the opposite, and
  // corsi-span depends on it — its "cue" IS the sequence to be reproduced, and
  // leaving it on screen during the response phase turns a memory task into a
  // copying task. The server cannot detect that; only this line prevents it.
  const cuePersists = task.cuePersists !== false;
  const showCue =
    phase === 'running' &&
    Boolean(task.CueView) &&
    Boolean(currentTrial) &&
    (stage === 'cue' || (stage === 'stimulus' && cuePersists));
  const isPractice = Boolean(currentTrial?.isPractice);
  const acceptsInput =
    typeof task.acceptsResponse === 'function' ? task.acceptsResponse(currentTrial || {}) : true;
  const isComposite = task.responseMode === 'composite';
  // The phase currently on screen — a block preamble (study list, filled
  // delay, instructions) that runs before a trial rather than during one.
  // Takes priority over the cue/stimulus below: a chained task's persistent
  // field would otherwise satisfy showStimulus even while stage is
  // 'preamble', since chainOnset ignores stage entirely.
  const phaseView = phase === 'running' && stage === 'preamble' ? runner.preamblePhase : null;
  // Response alternatives are part of the timed stimulus. Deriving no targets
  // before showStimulus also keeps rich option nodes out of the DOM during both
  // fixation and the inter-trial gap. A composite task owns its own response
  // surface — a tappable board, a set of pegs. Rendering ResponseTargets
  // alongside it would offer a second, wrong way to answer. A preamble is
  // never a response opportunity either, regardless of what showStimulus
  // says for a chained task.
  const targets =
    isComposite || !showStimulus || phaseView
      ? []
      : task.targetsFor
        ? task.targetsFor(currentTrial, block?.params)
        : task.targets;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'var(--bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-4)' }}>
        <div
          style={{
            flex: 1,
            height: 4,
            borderRadius: 'var(--radius-full)',
            background: 'var(--border-subtle)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.min(100, Math.max(0, overall * 100))}%`,
              background: 'var(--text-primary)',
              transition: 'width 0.2s linear',
            }}
          />
        </div>
        <span style={{ fontSize: 'var(--text-numeric-size)', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-tertiary)' }}>
          block {Math.min(blocksDone + 1, blockCount || 1)}/{blockCount || 1}
        </span>
        <button
          type="button"
          onClick={onExit}
          aria-label={`Exit ${title}`}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            padding: 'var(--space-1)',
          }}
        >
          <X size={18} strokeWidth={1.5} />
        </button>
      </div>

      {phase === 'running' && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 'var(--text-label-size)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 'var(--text-label-tracking)',
            color: isPractice ? 'var(--accent-primary)' : 'var(--text-tertiary)',
            minHeight: 'var(--space-4)',
          }}
        >
          {isPractice ? 'Practice — feedback on' : ''}
        </div>
      )}

      {phase === 'running' && task.BlockContextView && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: 'var(--space-1) var(--space-4) 0',
            flex: '0 0 auto',
          }}
        >
          <task.BlockContextView params={block?.params} />
        </div>
      )}

      <div
        style={{
          flex: 1,
          display: 'grid',
          placeItems: 'center',
          padding: 'var(--space-4)',
          minHeight: 0,
        }}
      >
        {phaseView ? (
          <phaseView.View params={block?.params} phase={phaseView} />
        ) : showCue || showStimulus ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-5)',
              minWidth: 0,
            }}
          >
            {/* params, not just the stimulus. A cue that paces itself has to
                pace on the SERVED interval: corsi-span sizes its cue stage as
                `presentation_interval_ms x length` in timing() while its view
                lights one block per `presentation_interval_ms`, and if the view
                cannot see params it falls back to 1000ms while the stage does
                not. A served 600ms then ends the stage before the last blocks
                are ever lit, and the response phase still demands a tap for
                each of them — an unanswerable trial that submits a
                normal-looking payload. */}
            {showCue && (
              <task.CueView stimulus={currentTrial.stimulus} params={block?.params} />
            )}
            {showStimulus &&
              (isComposite ? (
                <task.StimulusView
                  stimulus={currentTrial.stimulus}
                  params={block?.params}
                  trial={currentTrial}
                  stage={stage}
                  disabled={phase !== 'running' || !acceptsInput}
                  onFirstInteraction={runner.beginComposite}
                  onCommit={runner.commitComposite}
                />
              ) : (
                // `trial` is passed to simple views as well as composite ones,
                // and n-back depends on it: a stream with no fixation and no
                // gap repaints nothing at all when a letter repeats, so the
                // only thing that distinguishes trial i from trial i+1 is the
                // trial itself. Views that do not need it ignore it.
                <task.StimulusView stimulus={currentTrial.stimulus} trial={currentTrial} />
              ))}
          </div>
        ) : (
          <span style={{ fontSize: 28, color: 'var(--text-tertiary)' }}>
            {phase === 'running' && stage === 'fixation' ? '+' : ''}
          </span>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: '0 var(--space-4) var(--space-6)',
        }}
      >
        <div style={{ minHeight: 'var(--space-5)', fontSize: 'var(--text-body-size)', fontWeight: 700 }}>
          {feedback && (
            <span style={{ color: feedback.correct ? 'var(--success)' : 'var(--danger)' }}>
              {feedback.correct ? 'Correct' : 'Not quite'}
            </span>
          )}
        </div>
        <ResponseTargets
          targets={targets}
          onRespond={runner.respond}
          selectedTargetId={selectedTargetId}
          disabled={phase !== 'running' || !acceptsInput || selectedTargetId !== null}
        />
      </div>

      <InterruptPanel
        phase={phase}
        error={runner.error}
        onRetry={runner.retry}
        onResume={runner.resume}
        onExit={onExit}
      />
    </div>
  );
}
